// Master Drop scroll reveal: fades main sections in as they enter the viewport.
// Storefront only. Liquid does not load this file when request.design_mode is true; the checks
// below are a second guard. Content is never left hidden:
// - only sections below the fold at start are hidden (the script adds .md-reveal itself),
// - a geometry check on every scroll (window or .page-wrapper) and after 1.5 s reveals anything
//   that is in or above the viewport, even if IntersectionObserver never fires,
// - any editor event, late design mode or re-rendered section reveals everything and stops.
(() => {
  const root = document.documentElement;
  const script = document.currentScript;
  const inEditor = () =>
    Boolean(window.Shopify?.designMode) || root.classList.contains('shopify-design-mode');

  if (script?.dataset.designMode === 'true' || inEditor()) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const repeat = script?.dataset.repeat === 'true';
  const SAFETY_MS = 1500;
  const pending = new Set();
  let observer;
  let mutations;
  let queued = false;
  let stopped = false;

  const finish = (section) => {
    // Drop the reveal classes once the fade is done so no transform/opacity lingers on the section.
    if (repeat) return;
    const clear = (event) => {
      if (event && event.target !== section) return;
      section.removeEventListener('transitionend', clear);
      section.classList.remove('md-reveal', 'is-revealed');
    };
    section.addEventListener('transitionend', clear);
    window.setTimeout(clear, 1500);
  };

  const reveal = (section) => {
    section.classList.add('is-revealed');
    if (!repeat) {
      pending.delete(section);
      observer?.unobserve(section);
      finish(section);
    }
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    observer?.disconnect();
    mutations?.disconnect();
    document.removeEventListener('scroll', onScroll, { capture: true });
    window.removeEventListener('resize', onScroll);
    for (const section of document.querySelectorAll('.md-reveal')) {
      section.classList.remove('md-reveal', 'is-revealed');
    }
    pending.clear();
    root.classList.remove('md-reveal-ready');
  };

  const isReached = (section) => section.getBoundingClientRect().top < window.innerHeight;

  const sweep = () => {
    queued = false;
    if (inEditor()) return stop();
    for (const section of pending) if (isReached(section)) reveal(section);
  };

  function onScroll() {
    if (queued || stopped) return;
    queued = true;
    requestAnimationFrame(sweep);
  }

  const start = () => {
    if (inEditor()) return;
    const main = document.getElementById('MainContent');
    const sections = main ? main.querySelectorAll(':scope > .shopify-section') : [];
    if (sections.length === 0) return;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) reveal(entry.target);
          else if (repeat && !isReached(entry.target)) entry.target.classList.remove('is-revealed');
        }
      },
      // threshold 0: a ratio threshold can never be met by a section taller than the viewport.
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );

    for (const section of sections) {
      // Already on screen (or above it): leave it visible, never hide it.
      if (isReached(section) && !repeat) continue;
      section.classList.add('md-reveal');
      if (isReached(section)) section.classList.add('is-revealed');
      else pending.add(section);
      observer.observe(section);
    }
    if (pending.size === 0 && !repeat) return observer.disconnect();

    // Sections added or replaced later are not registered: make sure they are never hidden.
    mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element && node.classList.contains('md-reveal') && !pending.has(node)) {
            node.classList.remove('md-reveal');
          }
        }
        for (const node of record.removedNodes) pending.delete(node);
      }
    });
    mutations.observe(main, { childList: true });

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.setTimeout(sweep, SAFETY_MS);
    root.classList.add('md-reveal-ready');
  };

  for (const name of [
    'shopify:section:load',
    'shopify:section:unload',
    'shopify:section:select',
    'shopify:section:reorder',
    'shopify:block:select',
    'shopify:inspector:activate',
  ]) {
    document.addEventListener(name, stop);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
